import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../ThemeContext';
import ContactUsForm from './ContactUsForm';
import Checkmark from '../../assets/checkmark.svg';
import LogoSvg from '../../assets/logo.svg';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { authAPI } from '../../helpers/api_service';
import sessionManager from '../../helpers/session_manager';
import {
  handleOAuthLogout,
  detectOAuthProvider,
  getOAuthAccessToken,
  clearAllOAuthData
} from '../../helpers/oauth_logout';
import { getProfileCompanyPicUrl } from '../../helpers/url_helper';
import '../Subscription/Subscription.css';

const StandaloneContactSales = () => {
  const { mode } = useThemeMode();
  const location = useLocation();
  const navigate = useNavigate();

  const [userFullName, setUserFullName] = useState('');
  const [userProfilePic, setUserProfilePic] = useState('');
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);

  useEffect(() => {
    document.body.classList.remove('dark-mode', 'light-mode');
    document.body.classList.add(mode === 'dark' ? 'dark-mode' : 'light-mode');
  }, [mode]);

  useEffect(() => {
    const fullName = localStorage.getItem('userFullName') || '';
    const profilePic = localStorage.getItem('userProfilePic') || '';
    setUserFullName(fullName);
    setUserProfilePic(profilePic);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const optionsMenu = event.target.closest('[data-options-menu]');
      const optionsButton = event.target.closest('[data-options-button]');

      if (showOptionsMenu && !optionsMenu && !optionsButton) {
        setShowOptionsMenu(false);
      }
    };

    if (showOptionsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOptionsMenu]);

  const handleLogout = async () => {
    try {
      setShowOptionsMenu(false);

      const oauthProvider = detectOAuthProvider();
      if (oauthProvider) {
        const accessToken = getOAuthAccessToken(oauthProvider);
        await handleOAuthLogout(oauthProvider, accessToken);
      }

      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      sessionManager.clearSession();
      localStorage.removeItem('userData');
      localStorage.removeItem('userFullName');
      localStorage.removeItem('userProfilePic');
      clearAllOAuthData();
      navigate('/login');
    }
  };

  const planTitle = location.state?.planTitle || 'Enterprise Plan';
  const displayPrice = location.state?.displayPrice || 'Custom pricing';
  const billingCycle = location.state?.billingCycle || 'MONTHLY';

  const features = useMemo(() => {
    const formatted = location.state?.formattedDescription;
    if (!formatted) return [];
    return formatted
      .split('\n')
      .slice(1)
      .map((line) => line.trim())
      .filter(Boolean);
  }, [location.state?.formattedDescription]);

  return (
    <div
      className="new-subscription-page"
      style={{
        minHeight: '100vh',
        backgroundColor: mode === 'dark' ? '#0f0f0f' : '#f5f7f9',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <div className="new-subscription-header">
        <div className="header-content">
          <div className="logo-section">
            <img src={LogoSvg} alt="Complimate Logo" style={{ height: 50, verticalAlign: 'middle' }} />
          </div>
          <div className="user-profile-section">
            <div
              className="user-avatar"
              style={{
                backgroundImage: userProfilePic ? `url(${getProfileCompanyPicUrl(userProfilePic)})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                width: '33px',
                height: '33px',
                borderRadius: '50%',
                marginRight: '8px',
                flexShrink: 0,
                backgroundColor: !userProfilePic ? '#009391' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {!userProfilePic && (
                <span style={{ color: '#fff', fontSize: '14px', fontWeight: '600' }}>
                  {userFullName ? userFullName.charAt(0).toUpperCase() : 'U'}
                </span>
              )}
            </div>
            <div className="user-name">{userFullName || 'User'}</div>
            <div className="user-actions">
              <div style={{ position: 'relative' }}>
                <button
                  className="header-icon-btn"
                  data-options-button
                  onClick={() => setShowOptionsMenu(!showOptionsMenu)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '33px',
                    height: '33px',
                    border: 'none',
                    background: '#009391',
                    borderRadius: '13px',
                    cursor: 'pointer',
                    padding: 0,
                    margin: 0
                  }}
                >
                  <MoreVertIcon
                    style={{
                      fontSize: '23px',
                      color: '#fff',
                    }}
                  />
                </button>

                {showOptionsMenu && (
                  <div
                    data-options-menu
                    style={{
                      position: 'absolute',
                      top: '45px',
                      right: '0px',
                      backgroundColor: mode === 'dark' ? '#333' : '#fff',
                      border: `1px solid ${mode === 'dark' ? '#555' : '#ddd'}`,
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      padding: '8px 0',
                      minWidth: '160px',
                      zIndex: 1000
                    }}
                  >
                    <button
                      onClick={handleLogout}
                      style={{
                        width: '100%',
                        padding: '8px 16px',
                        border: 'none',
                        background: 'transparent',
                        color: mode === 'dark' ? '#fff' : '#333',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = mode === 'dark' ? '#555' : '#f5f5f5';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                      }}
                    >
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          flex: 1,
          padding: '32px',
        }}
      >
        <Container fluid className="px-0">
          <Row className="g-4">
            <Col
              xl={5}
              lg={5}
              md={12}
              style={{
                minWidth: '360px',
              }}
            >
              <Card
                className="h-100"
                style={{
                  borderRadius: '16px',
                  border: 'none',
                  boxShadow: mode === 'dark'
                    ? '0 12px 24px rgba(0, 0, 0, 0.4)'
                    : '0 12px 24px rgba(38, 195, 192, 0.12)',
                  backgroundColor: mode === 'dark' ? '#151515' : '#ffffff',
                }}
              >
                <Card.Body style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ marginBottom: '16px' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '6px 14px',
                        borderRadius: '999px',
                        backgroundColor: 'rgba(38, 195, 192, 0.12)',
                        color: '#26C3C0',
                        fontWeight: 600,
                        fontSize: '13px',
                        letterSpacing: '0.4px',
                        textTransform: 'uppercase',
                      }}
                    >
                      Plan Details
                    </span>
                  </div>
                  <h3
                    style={{
                      color: mode === 'dark' ? '#ffffff' : '#001219',
                      fontWeight: 700,
                      fontSize: '24px',
                      marginBottom: '12px',
                    }}
                  >
                    {planTitle}
                  </h3>
                  <p
                    style={{
                      color: mode === 'dark' ? '#8f9aa3' : '#5c6b73',
                      marginBottom: '24px',
                      lineHeight: 1.6,
                    }}
                  >
                    Share your requirements with our team. We’ll craft a tailored compliance solution and pricing to match your organisation’s needs.
                  </p>

                  <div
                    style={{
                      borderRadius: '14px',
                      padding: '18px',
                      background: mode === 'dark'
                        ? 'linear-gradient(135deg, rgba(38,195,192,0.15), rgba(17,24,39,0.4))'
                        : 'linear-gradient(135deg, rgba(38,195,192,0.12), rgba(38,195,192,0.05))',
                      marginBottom: '24px',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '14px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.4px',
                        color: mode === 'dark' ? '#b9d6d6' : '#2f6f6e',
                        marginBottom: '4px',
                        fontWeight: 600,
                      }}
                    >
                      Investment
                    </div>
                    <div
                      style={{
                        fontSize: '26px',
                        fontWeight: 700,
                        color: mode === 'dark' ? '#FFFFFF' : '#002b3d',
                      }}
                    >
                      {displayPrice || 'Custom pricing'}
                    </div>
                    <div
                      style={{
                        fontSize: '13px',
                        color: mode === 'dark' ? '#8f9aa3' : '#5c6b73',
                      }}
                    >
                      Preferred billing: {billingCycle}
                    </div>
                  </div>

                  {features.length > 0 && (
                    <div style={{ marginBottom: 'auto' }}>
                      <h5
                        style={{
                          fontSize: '15px',
                          fontWeight: 600,
                          color: mode === 'dark' ? '#E4F1F1' : '#12343b',
                          marginBottom: '12px',
                        }}
                      >
                        What’s included
                      </h5>
                      <ul
                        style={{
                          listStyle: 'none',
                          padding: 0,
                          margin: 0,
                          display: 'grid',
                          gap: '10px',
                        }}
                      >
                        {features.map((feature, index) => (
                          <li
                            key={`${feature}-${index}`}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              fontSize: '14px',
                              color: mode === 'dark' ? '#d1e4e4' : '#12343b',
                              lineHeight: 1.5,
                            }}
                          >
                            <img
                              src={Checkmark}
                              alt=""
                              style={{ width: '16px', height: '16px' }}
                            />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col xl={7} lg={7} md={12}>
              <Card
                className="h-100"
                style={{
                  borderRadius: '16px',
                  border: 'none',
                  boxShadow: mode === 'dark'
                    ? '0 12px 24px rgba(0, 0, 0, 0.4)'
                    : '0 12px 24px rgba(38, 195, 192, 0.12)',
                  backgroundColor: mode === 'dark' ? '#151515' : '#ffffff',
                }}
              >
                <Card.Body style={{ padding: '32px' }}>
                  <div style={{ marginBottom: '24px' }}>
                    <h4
                      style={{
                        marginBottom: '8px',
                        color: mode === 'dark' ? '#ffffff' : '#001219',
                        fontWeight: 700,
                      }}
                    >
                      Tell us about your requirements
                    </h4>
                    <p
                      style={{
                        color: mode === 'dark' ? '#8f9aa3' : '#5c6b73',
                        marginBottom: 0,
                      }}
                    >
                      Provide a few details and our sales team will reach out with a tailored proposal.
                    </p>
                  </div>
                  <ContactUsForm
                    submitLabel="Contact Sales"
                    secondaryAction={
                      <Button
                        variant="outline-secondary"
                        onClick={() => navigate(-1)}
                        style={{
                          borderRadius: '10px',
                          borderColor: mode === 'dark' ? '#3b3b3b' : '#d4dde1',
                          color: mode === 'dark' ? '#d4dde1' : '#3b3b3b',
                          fontWeight: 500,
                          padding: '12px 20px',
                        }}
                      >
                        Cancel
                      </Button>
                    }
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
};

export default StandaloneContactSales;

